/**
 * @file analytics.js
 * @description Integración de PostHog para Evazard (tarea 004): analítica de comportamiento
 * + rastreo de errores, con **consentimiento explícito** (opt-out por defecto) y atribución
 * a `user_id` + `business_id` (multi-tenancy). Concentra el uso de `posthog` en este módulo
 * para no esparcirlo por la app y facilitar los tests.
 *
 * Reglas de la tarea 004 que este módulo garantiza:
 * - No se captura nada hasta que el usuario da su consentimiento (`opt_out_capturing_by_default`).
 * - No se envía PII: en `identify` solo van IDs internos y el rol, nunca email/nombre.
 * - Sin API key (p. ej. dev sin configurar) el módulo queda como no-op silencioso.
 */
import posthog from 'posthog-js';

let ready = false;

/**
 * Clave propia donde persistimos la decisión del banner de cookies. No dependemos solo del
 * estado interno de PostHog porque `posthog.reset()` (que corre en cada logout) lo borra, y el
 * banner reaparecía en cada ciclo de sesión. Con esta clave la decisión es a nivel dispositivo
 * y solo se pregunta una vez.
 */
const CONSENT_STORAGE_KEY = 'evazard_analytics_consent';

/** Lee la decisión persistida: 'granted' | 'denied' | null (nunca decidido). */
const readStoredConsent = () => {
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
};

const writeStoredConsent = (status) => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, status);
  } catch {
    /* localStorage no disponible (modo privado, etc.): seguimos sin persistir. */
  }
};

/** Re-aplica en PostHog la decisión ya tomada (tras init o tras un reset). */
const applyStoredConsent = () => {
  if (!ready) return;
  const stored = readStoredConsent();
  if (stored === 'granted') posthog.opt_in_capturing();
  else if (stored === 'denied') posthog.opt_out_capturing();
};

/** true una vez que PostHog quedó inicializado; false si no hay API key (no-op). */
export const isAnalyticsReady = () => ready;

/**
 * Inicializa PostHog. Se llama una sola vez desde `index.js`, antes de renderizar React,
 * así que para cuando montan los componentes `ready` ya es true.
 * Las claves se hornean en build-time (CRA): Infisical en prod, `.env` en local.
 */
export const initAnalytics = () => {
  const key = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
  if (ready || !key) return;

  posthog.init(key, {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',   // solo crea perfil para usuarios identificados (ahorra cuota)
    capture_exceptions: true,             // rastreo automático de errores (window.onerror + unhandledrejection)
    capture_pageview: 'history_change',   // pageviews SPA automáticos vía History API (React Router)
    opt_out_capturing_by_default: true,   // NO captura nada hasta el consentimiento explícito
    // Session replay: por defecto PostHog NO graba (el toggle vive en Project Settings > Recordings).
    // Cuando se active, esta config garantiza que NO se filtre PII: Evazard maneja datos reales de
    // clientes (nombre/teléfono/dirección) y montos, así que enmascaramos por defecto.
    session_recording: {
      maskAllInputs: true,   // enmascara cada valor de input (montos, datos de cliente al capturar)
      maskTextSelector: '*', // enmascara el texto renderizado completo (nombres/teléfonos/montos en tablas y recibos)
    },
  });
  ready = true;

  // Si el usuario ya decidió en una sesión anterior, respetamos esa decisión sin volver a preguntar.
  applyStoredConsent();
};

// --- Consentimiento (device-level, persistido por PostHog en localStorage) ---

/**
 * Estado del consentimiento: 'granted' | 'denied' | 'pending'. Nuestra clave persistida manda
 * (sobrevive a `posthog.reset()` en logout); PostHog es solo el fallback.
 */
export const getConsentStatus = () => {
  const stored = readStoredConsent();
  if (stored) return stored;
  return ready ? posthog.get_explicit_consent_status() : 'pending';
};

/** El usuario acepta: empieza la captura y se persiste la decisión. */
export const grantAnalyticsConsent = () => {
  writeStoredConsent('granted');
  if (ready) posthog.opt_in_capturing();
};

/** El usuario rechaza: no se captura nada y se persiste la decisión. */
export const denyAnalyticsConsent = () => {
  writeStoredConsent('denied');
  if (ready) posthog.opt_out_capturing();
};

// --- Eventos de acciones clave ---

/**
 * Registra una acción del usuario (p. ej. 'sale_saved', 'expense_created').
 * @param {string} action  Nombre del evento en snake_case, en inglés y estable (es un identificador, NO texto de UI).
 * @param {Object} [properties]  Metadatos NO sensibles: contadores/booleanos. **Nunca** PII, montos,
 *   nombres/teléfonos de cliente, tokens ni payloads (ver alcance de la tarea 004).
 */
export const trackAction = (action, properties = {}) => {
  if (!ready) return;
  posthog.capture(action, properties);
};

// --- Identidad (multi-tenancy). Sin PII: solo IDs internos + rol. ---

/**
 * Atribuye los eventos al usuario y su negocio. Idempotente.
 * @param {{id: number|string, business_id?: number|string, role?: string}} user
 * @param {{name?: string}} [business]
 */
export const identifyUser = (user, business) => {
  if (!ready || !user?.id) return;
  posthog.identify(String(user.id), {
    business_id: user.business_id ?? null,
    role: user.role ?? null,
  });
  if (user.business_id) {
    posthog.group(
      'business',
      String(user.business_id),
      business?.name ? { name: business.name } : undefined
    );
  }
};

/** Limpia la identidad al cerrar sesión para no mezclar usuarios en el mismo navegador. */
export const resetAnalytics = () => {
  if (!ready) return;
  posthog.reset();
  // `reset()` también borra el consentimiento a nivel dispositivo; la decisión del banner es del
  // navegador, no de la sesión, así que la re-aplicamos para no volver a preguntar tras el logout.
  applyStoredConsent();
};
